import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import photo from '~/shared/ui/photoBox.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { PlayerListRow } from '../model/playerView';

import styles from './ClipPlayer.module.css';

const SKELETON_ROWS = 3;

interface PlayerListProps {
  readonly rows: readonly PlayerListRow[];
  readonly clipId: number;
  readonly capacity: { readonly count: number; readonly max: number } | null;
  readonly pending: boolean;
  /** Theatre mode folds the column away without unmounting it. */
  readonly folded: boolean;
  readonly onSelect: (clipId: number) => void;
}

/** The movement's recordings, newest first, with who added each one. */
export function PlayerList({ rows, clipId, capacity, pending, folded, onSelect }: PlayerListProps) {
  const { t } = useTranslation();
  const gate = useSkeletonGate(pending);
  return (
    <nav
      aria-busy={gate.waiting}
      aria-label={t('web:clipPlayer.list.label')}
      className={`${String(styles.list)} ${folded ? String(styles.folded) : ''}`}
    >
      <p className={`${String(styles.eyebrow)} ${String(styles.listEyebrow)} ${gate.reveal}`}>
        {gate.waiting || capacity === null
          ? ''
          : t('web:clipPlayer.list.title', { count: capacity.count, cap: capacity.max })}
      </p>
      <div className={`${String(styles.listRows)} ${gate.reveal}`}>
        {gate.waiting ? (
          <>
            <span className={hidden.srOnly}>{t('web:clipPlayer.states.loading')}</span>
            {gate.skeleton
              ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                  <span className={styles.listRowWait} key={index}>
                    <i className={`${String(skel.bar)} ${String(styles.thumb)}`} />
                    <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                  </span>
                ))
              : null}
          </>
        ) : (
          rows.map((row) => (
            <button
              aria-current={row.clipId === clipId ? 'true' : undefined}
              className={`${String(styles.listRow)} ${row.clipId === clipId ? String(styles.listRowOn) : ''}`}
              key={row.clipId}
              onClick={() => {
                onSelect(row.clipId);
              }}
              type="button"
            >
              <span className={`${String(styles.thumb)} ${row.wide ? String(styles.thumbWide) : ''}`}>
                {row.posterUrl === null ? null : (
                  <img
                    alt=""
                    className={`${String(photo.photo)} ${String(styles.thumbImg)}`}
                    decoding="async"
                    loading="lazy"
                    src={row.posterUrl}
                  />
                )}
              </span>
              <span className={styles.listText}>
                <span className={styles.listDate}>{row.dateShort}</span>
                <span className={styles.listMeta}>{row.meta}</span>
                <span className={`${String(styles.by)} ${row.by.isYou ? String(styles.byYou) : ''}`}>
                  <AvatarChip avatarUrl={row.by.avatarUrl} initials={row.by.initials} variant="clipSmall" />
                  {row.by.name}
                </span>
              </span>
              {row.unread ? (
                <span className={styles.dot}>
                  <span className={hidden.srOnly}>{t('web:forma.list.unread')}</span>
                </span>
              ) : null}
            </button>
          ))
        )}
      </div>
    </nav>
  );
}
